import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Grid,
  Icon,
  SIZE,
  Skeleton,
  Stack,
  Text,
  useForm,
} from '@quinyx/ui';
import * as z from 'zod';

import {
  MIN_REASON_LENGTH,
  TASK_ID_IN_TEXT_PATTERN,
  TICKET_PATTERN,
} from '@/constants/support-task';
import ActionCard from './ActionCard';
import ConfirmDeleteTaskModal from './ConfirmDeleteTaskModal';
import { CodeValue, TaskField } from './TaskField';
import TaskSummary from './TaskSummary';
import { extractTaskId, humanDate } from './deleteTaskHelpers';
import { useTranslate } from '../../hooks/translate';
import {
  useGetSupportTask,
  useDeleteSupportTask,
} from '../../hooks/support/task';

type DeleteTaskCardProps = {
  domainId: string;
  customerName?: string;
};

const DeleteTaskCard = ({ domainId, customerName }: DeleteTaskCardProps) => {
  const { t } = useTranslate();
  const customerLabel = customerName
    ? t('support.deleteTask.customerWithDomain', { customerName, domainId })
    : t('support.deleteTask.domainOnly', { domainId });
  const { findTask, task, isFinding, isNotFound, resetTask, lookedUpTaskId } =
    useGetSupportTask();
  const { deleteTask, deleteResult, isDeleting, resetDelete } =
    useDeleteSupportTask();
  const [isConfirmOpen, setConfirmOpen] = useState(false);

  const lookupForm = useForm({
    schema: z.object({
      taskId: z.preprocess(
        extractTaskId,
        z
          .string()
          .regex(
            TASK_ID_IN_TEXT_PATTERN,
            t('support.deleteTask.invalidTaskId'),
          ),
      ),
    }),
    defaultValues: { taskId: '' },
  });

  const reasonForm = useForm({
    schema: z.object({
      ticket: z
        .string()
        .trim()
        .regex(TICKET_PATTERN, t('support.deleteTask.invalidTicket')),
      reason: z
        .string()
        .trim()
        .min(MIN_REASON_LENGTH, t('support.deleteTask.reasonTooShort')),
    }),
    defaultValues: { ticket: '', reason: '' },
  });

  const lookupValue = lookupForm.watch('taskId');
  const reasonValue = reasonForm.watch('reason');
  const ticketValue = reasonForm.watch('ticket');
  useEffect(() => {
    if (lookedUpTaskId && extractTaskId(lookupValue) !== lookedUpTaskId) {
      resetTask();
    }
  }, [lookupValue, lookedUpTaskId, resetTask]);

  const handleLookup = async ({ taskId }: { taskId: string }) => {
    resetDelete();
    try {
      await findTask({ taskId, domainId });
    } catch {
      return;
    }
    reasonForm.setFocus('ticket');
  };

  const handleClear = () => {
    resetTask();
    resetDelete();
    lookupForm.reset();
    reasonForm.reset({ ticket: reasonForm.getValues('ticket'), reason: '' });
    lookupForm.setFocus('taskId');
  };

  const handleConfirmDelete = async () => {
    if (!task) return;
    const { reason, ticket } = reasonForm.getValues();
    try {
      await deleteTask({
        taskId: task._id,
        reason: reason.trim(),
        ticket: ticket.trim().toUpperCase(),
        domainId,
      });
    } catch {
      return;
    }
    setConfirmOpen(false);
    resetTask();
    lookupForm.reset();
    reasonForm.reset({ ticket: ticket.trim().toUpperCase(), reason: '' });
  };

  const renderLookupResult = () => {
    if (isFinding) {
      return <Skeleton type="round" height="240px" />;
    }
    if (isNotFound) {
      return (
        <Alert data-test-id="support-delete-task-not-found" type="info">
          <Text variant="smallRegular">
            {t('support.deleteTask.notFound', { customer: customerLabel })}
          </Text>
        </Alert>
      );
    }
    if (!task) {
      return null;
    }

    const {
      deletedAt,
      deletedBy,
      deletionTicket,
      deletionReason,
      isRecurring,
      isScheduled,
      scheduledFor,
      distributedTotal,
    } = task;
    const deletionDetail = [
      deletionTicket,
      deletionReason,
      deletedBy && `${t('support.deleteTask.alreadyDeletedBy')} ${deletedBy}`,
    ]
      .filter(Boolean)
      .join(' · ');

    return (
      <Stack spacing={SIZE.M} style={{ width: '100%' }}>
        <TaskSummary task={task} />

        {deletedAt ? (
          <Alert data-test-id="support-delete-task-already-deleted" type="info">
            <Stack spacing={SIZE.XS}>
              <Text variant="smallHeavy">
                {t('support.deleteTask.alreadyDeleted', {
                  date: humanDate(deletedAt),
                })}
              </Text>
              {deletionDetail && (
                <Text variant="smallRegular">{deletionDetail}</Text>
              )}
            </Stack>
          </Alert>
        ) : (
          <>
            <Alert data-test-id="support-delete-task-impact" type="warning">
              <Stack spacing={SIZE.XS}>
                <Text variant="smallHeavy">
                  {t('support.deleteTask.impactTitle')}
                </Text>
                <Text as="div" variant="smallRegular">
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    <li>
                      {t('support.deleteTask.impactRecipients', {
                        total: distributedTotal ?? 0,
                      })}
                    </li>
                    {isRecurring && (
                      <li>{t('support.deleteTask.impactRecurring')}</li>
                    )}
                    {isScheduled && scheduledFor && (
                      <li>
                        {t('support.deleteTask.impactScheduled', {
                          date: humanDate(scheduledFor),
                        })}
                      </li>
                    )}
                    <li>{t('support.deleteTask.impactCompliance')}</li>
                  </ul>
                </Text>
                <Text variant="smallRegular">
                  {t('support.deleteTask.impactLastResort')}
                </Text>
              </Stack>
            </Alert>

            <Form methods={reasonForm} onSubmit={() => setConfirmOpen(true)}>
              <Stack spacing={SIZE.M}>
                <Grid columns={[1, 1, 3]} gap={SIZE.M}>
                  <Form.TextInput
                    data-test-id="support-delete-task-ticket-input"
                    name="ticket"
                    label={t('support.deleteTask.ticket')}
                    placeholder="QRND-1234"
                  />
                </Grid>
                <Form.TextAreaInput
                  data-test-id="support-delete-task-reason-input"
                  name="reason"
                  minRows={3}
                  label={t('support.deleteTask.reason')}
                  info={t('support.deleteTask.reasonCounter', {
                    length: (reasonValue || '').trim().length,
                    min: MIN_REASON_LENGTH,
                  })}
                />
                <Stack alignItems="flex-start">
                  <Button
                    data-test-id="support-delete-task-delete-button"
                    text={t('support.deleteTask.deletePermanently')}
                    intent="danger"
                    variant="filled"
                    leftIcon="delete"
                    onClick={reasonForm.handleSubmit(() =>
                      setConfirmOpen(true),
                    )}
                  />
                </Stack>
              </Stack>
            </Form>
          </>
        )}
      </Stack>
    );
  };

  const renderDeleteResult = () => {
    if (!deleteResult) {
      return null;
    }

    const {
      task: { _id: deletedTaskId, name: deletedTaskName },
      distributionsDeleted,
      compliancesDeleted,
    } = deleteResult;

    return (
      <Alert data-test-id="support-delete-task-success" type="positive">
        <Stack spacing={SIZE.S}>
          <Text variant="smallHeavy">
            {t('support.deleteTask.deleted', {
              name: deletedTaskName ?? deletedTaskId,
            })}
          </Text>
          <Grid columns={[1, 1, 3]} gap={SIZE.M}>
            <TaskField label={t('support.deleteTask.taskId')}>
              <CodeValue>{deletedTaskId}</CodeValue>
            </TaskField>
            <TaskField label={t('support.deleteTask.distributionsRemoved')}>
              {distributionsDeleted}
            </TaskField>
            <TaskField label={t('support.deleteTask.compliancesRemoved')}>
              {compliancesDeleted}
            </TaskField>
          </Grid>
        </Stack>
      </Alert>
    );
  };

  return (
    <ActionCard
      title={t('support.deleteTask.title')}
      description={t('support.deleteTask.description')}>
      <Stack spacing={SIZE.M} style={{ width: '100%' }}>
        {renderDeleteResult()}

        <Form methods={lookupForm} onSubmit={handleLookup}>
          <Stack direction="row" spacing={SIZE.S} alignItems="flex-start">
            <Form.TextInput
              data-test-id="support-delete-task-id-input"
              name="taskId"
              autoFocus
              autoComplete="off"
              label={t('support.deleteTask.taskIdOrLink')}
              leftAdornment={<Icon icon="search" />}
            />
            <Stack
              direction="row"
              spacing={SIZE.S}
              fullWidth={false}
              pt={SIZE.L}
              className="white-space-nowrap">
              <Form.SubmitButton
                data-test-id="support-delete-task-find-button"
                text={t('support.deleteTask.findTask')}
                isLoading={isFinding}
                disabled={!lookupValue}
              />
              {(task || isNotFound || deleteResult) && (
                <Button
                  data-test-id="support-delete-task-clear-button"
                  text={t('support.deleteTask.clear')}
                  variant="text"
                  onClick={handleClear}
                />
              )}
            </Stack>
          </Stack>
        </Form>

        {renderLookupResult()}
      </Stack>

      <ConfirmDeleteTaskModal
        open={isConfirmOpen}
        task={task}
        customerName={customerName}
        domainId={domainId}
        ticket={(ticketValue || '').trim().toUpperCase()}
        reason={(reasonValue || '').trim()}
        isDeleting={isDeleting}
        onOpenChange={setConfirmOpen}
        onConfirm={handleConfirmDelete}
      />
    </ActionCard>
  );
};

export default DeleteTaskCard;
